/**
 * NotificationSoundService - Professional sound notifications for chat messages
 * Handles audio notifications with user preferences and browser compatibility
 */

export interface NotificationSoundOptions {
  enabled: boolean
  volume: number // 0-1
  soundType: 'subtle' | 'default' | 'tone'
}

export class NotificationSoundService {
  private audioContext: AudioContext | null = null
  private isAudioInitialized = false
  private userPreferences: NotificationSoundOptions = {
    enabled: true,
    volume: 0.5,
    soundType: 'subtle'
  }

  constructor() {
    this.loadUserPreferences()
    this.initializeAudio()
    this.setupMobileAudioUnlock()
  }

  /**
   * Setup mobile audio unlock on first user interaction
   */
  private setupMobileAudioUnlock(): void {
    if (typeof window === 'undefined') return

    const unlockAudio = async () => {
      await this.resumeAudioContext()
      // Remove listeners after first interaction
      document.removeEventListener('touchstart', unlockAudio)
      document.removeEventListener('touchend', unlockAudio)
      document.removeEventListener('click', unlockAudio)
      document.removeEventListener('keydown', unlockAudio)
      console.log('🔊 Mobile audio unlocked on user interaction')
    }

    // Listen for first user interaction to unlock audio - broader event coverage
    document.addEventListener('touchstart', unlockAudio, { once: true, passive: true })
    document.addEventListener('touchend', unlockAudio, { once: true, passive: true })
    document.addEventListener('click', unlockAudio, { once: true, passive: true })
    document.addEventListener('keydown', unlockAudio, { once: true, passive: true })

    // Also try to unlock audio when the page becomes visible
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.resumeAudioContext().catch(() => {}) // Silent fail if already unlocked
      }
    }, { once: true, passive: true })
  }

  /**
   * Initialize Web Audio API
   */
  private async initializeAudio(): Promise<void> {
    try {
      // Create audio context only when needed to respect autoplay policies
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      }

      if (this.audioContext.state === 'suspended') {
        // Will be resumed on first user interaction
        console.log('🔊 Audio context suspended - will resume on first user interaction')
      }

      this.isAudioInitialized = true
    } catch (error) {
      console.warn('🔊 Failed to initialize audio context:', error)
      this.isAudioInitialized = false
    }
  }

  /**
   * Resume audio context (call on first user interaction) - Mobile optimized
   */
  async resumeAudioContext(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume()
        console.log('🔊 Audio context resumed')
      } catch (error) {
        console.warn('🔊 Failed to resume audio context:', error)
        // On mobile, try to recreate audio context
        if (/android|iphone|ipad|ipod/i.test(navigator.userAgent)) {
          try {
            this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
            console.log('🔊 Recreated audio context for mobile')
          } catch (recreateError) {
            console.warn('🔊 Failed to recreate audio context on mobile:', recreateError)
          }
        }
      }
    }
  }

  /**
   * Play incoming call sound (for voice and video calls)
   */
  async playIncomingCallSound(callType: 'voice' | 'video' = 'voice'): Promise<void> {
    if (!this.userPreferences.enabled) {
      // Still trigger vibration even if audio is disabled
      if ('vibrate' in navigator) {
        const pattern = callType === 'video' ? [200, 100, 200, 100, 200] : [300, 100, 300]
        navigator.vibrate(pattern)
      }
      return
    }

    // Initialize audio if not already done
    if (!this.isAudioInitialized || !this.audioContext) {
      await this.initializeAudio()
    }

    if (!this.audioContext) {
      console.warn('🔊 Could not initialize audio context for incoming call')
      this.fallbackNotificationSound()
      return
    }

    try {
      // Resume audio context if suspended (required for mobile)
      await this.resumeAudioContext()

      // Additional mobile compatibility check with fallback
      if (this.audioContext.state !== 'running') {
        console.warn('🔊 Audio context not running on mobile device, using fallback')
        this.fallbackNotificationSound()
        return
      }

      // Create oscillator for incoming call tone (different from notification)
      const oscillator = this.audioContext.createOscillator()
      const gainNode = this.audioContext.createGain()

      // Configure incoming call sound based on call type
      if (callType === 'video') {
        // Video call: higher pitch, double beep pattern
        oscillator.frequency.setValueAtTime(1000, this.audioContext.currentTime)
        oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime + 0.3)
        oscillator.frequency.setValueAtTime(1000, this.audioContext.currentTime + 0.6)

        gainNode.gain.setValueAtTime(0, this.audioContext.currentTime)
        gainNode.gain.linearRampToValueAtTime(this.userPreferences.volume * 0.6, this.audioContext.currentTime + 0.1)
        gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.25)
        gainNode.gain.linearRampToValueAtTime(this.userPreferences.volume * 0.6, this.audioContext.currentTime + 0.4)
        gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.55)
        gainNode.gain.linearRampToValueAtTime(this.userPreferences.volume * 0.6, this.audioContext.currentTime + 0.7)
        gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 1.0)
      } else {
        // Voice call: lower pitch, single ring pattern
        oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime)
        oscillator.frequency.setValueAtTime(600, this.audioContext.currentTime + 0.5)

        gainNode.gain.setValueAtTime(0, this.audioContext.currentTime)
        gainNode.gain.linearRampToValueAtTime(this.userPreferences.volume * 0.5, this.audioContext.currentTime + 0.1)
        gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.4)
        gainNode.gain.linearRampToValueAtTime(this.userPreferences.volume * 0.5, this.audioContext.currentTime + 0.6)
        gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 1.0)
      }

      // Use a sine wave for a pleasant tone
      oscillator.type = 'sine'

      // Connect nodes
      oscillator.connect(gainNode)
      gainNode.connect(this.audioContext.destination)

      // Play sound
      oscillator.start(this.audioContext.currentTime)
      oscillator.stop(this.audioContext.currentTime + 1.0)

      console.log(`🔊 Incoming ${callType} call sound played`)
    } catch (error) {
      console.warn(`🔊 Failed to play incoming ${callType} call sound:`, error)
      // Fallback to simple beep
      this.fallbackNotificationSound()
    }
  }

  /**
   * Play notification sound using Web Audio API (Mobile-optimized)
   */
  async playNotificationSound(): Promise<void> {
    if (!this.userPreferences.enabled || !this.isAudioInitialized || !this.audioContext) {
      return
    }

    try {
      // Resume audio context if suspended (required for mobile)
      await this.resumeAudioContext()

      // Additional mobile compatibility check
      if (this.audioContext.state !== 'running') {
        console.warn('🔊 Audio context not running on mobile device')
        return
      }

      // Create oscillator for subtle notification tone
      const oscillator = this.audioContext.createOscillator()
      const gainNode = this.audioContext.createGain()

      // Configure sound based on preference
      switch (this.userPreferences.soundType) {
        case 'subtle':
          oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime)
          oscillator.frequency.exponentialRampToValueAtTime(400, this.audioContext.currentTime + 0.1)
          gainNode.gain.setValueAtTime(0, this.audioContext.currentTime)
          gainNode.gain.linearRampToValueAtTime(this.userPreferences.volume * 0.3, this.audioContext.currentTime + 0.01)
          gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 0.15)
          break

        case 'default':
          oscillator.frequency.setValueAtTime(523, this.audioContext.currentTime) // C5
          oscillator.frequency.setValueAtTime(659, this.audioContext.currentTime + 0.1) // E5
          gainNode.gain.setValueAtTime(0, this.audioContext.currentTime)
          gainNode.gain.linearRampToValueAtTime(this.userPreferences.volume * 0.4, this.audioContext.currentTime + 0.01)
          gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 0.2)
          break

        case 'tone':
          oscillator.frequency.setValueAtTime(1000, this.audioContext.currentTime)
          gainNode.gain.setValueAtTime(0, this.audioContext.currentTime)
          gainNode.gain.linearRampToValueAtTime(this.userPreferences.volume * 0.5, this.audioContext.currentTime + 0.05)
          gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 0.3)
          break
      }

      // Use a sine wave for a pleasant tone
      oscillator.type = 'sine'

      // Connect nodes
      oscillator.connect(gainNode)
      gainNode.connect(this.audioContext.destination)

      // Play sound
      oscillator.start(this.audioContext.currentTime)
      oscillator.stop(this.audioContext.currentTime + 0.3)

      console.log('🔊 Notification sound played')
    } catch (error) {
      console.warn('🔊 Failed to play notification sound:', error)
      // Fallback to simple beep
      this.fallbackNotificationSound()
    }
  }

  /**
   * Fallback notification using HTML5 audio (Mobile-compatible)
   */
  private fallbackNotificationSound(): void {
    try {
      // Mobile-friendly fallback: Use Vibration API if available
      if ('vibrate' in navigator) {
        navigator.vibrate([100, 50, 100]) // Short vibration pattern
        console.log('🔊 Using vibration as audio fallback on mobile')
      }

      // Create a simple data URL audio for fallback
      const audioData = this.generateSimpleBeep()
      const audio = new Audio(audioData)
      audio.volume = this.userPreferences.volume

      // Mobile-specific audio handling
      const playPromise = audio.play()
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn('🔊 Fallback notification sound failed:', error)
          // Try vibration again as last resort
          if ('vibrate' in navigator) {
            navigator.vibrate(200)
          }
        })
      }
    } catch (error) {
      console.warn('🔊 All notification sound methods failed:', error)
      // Last resort: vibration only
      if ('vibrate' in navigator) {
        navigator.vibrate([150, 100, 150])
      }
    }
  }

  /**
   * Generate a simple beep sound as data URL
   */
  private generateSimpleBeep(): string {
    // This creates a very basic wave file header for a simple tone
    // For production, you might want to use a pre-recorded notification sound
    const sampleRate = 22050
    const duration = 0.2
    const frequency = 800
    const samples = sampleRate * duration

    const buffer = new ArrayBuffer(44 + samples * 2)
    const view = new DataView(buffer)

    // WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i))
      }
    }

    writeString(0, 'RIFF')
    view.setUint32(4, 36 + samples * 2, true)
    writeString(8, 'WAVE')
    writeString(12, 'fmt ')
    view.setUint32(16, 16, true)
    view.setUint16(20, 1, true)
    view.setUint16(22, 1, true)
    view.setUint32(24, sampleRate, true)
    view.setUint32(28, sampleRate * 2, true)
    view.setUint16(32, 2, true)
    view.setUint16(34, 16, true)
    writeString(36, 'data')
    view.setUint32(40, samples * 2, true)

    // Generate sine wave
    for (let i = 0; i < samples; i++) {
      const sample = Math.sin(2 * Math.PI * frequency * i / sampleRate) * 0.3
      view.setInt16(44 + i * 2, sample * 32767, true)
    }

    const blob = new Blob([buffer], { type: 'audio/wav' })
    return URL.createObjectURL(blob)
  }

  /**
   * Update user preferences
   */
  updatePreferences(preferences: Partial<NotificationSoundOptions>): void {
    this.userPreferences = { ...this.userPreferences, ...preferences }
    this.saveUserPreferences()
    console.log('🔊 Notification preferences updated:', this.userPreferences)
  }

  /**
   * Get current preferences
   */
  getPreferences(): NotificationSoundOptions {
    return { ...this.userPreferences }
  }

  /**
   * Test notification sound
   */
  async testSound(): Promise<void> {
    await this.playNotificationSound()
  }

  /**
   * Load user preferences from localStorage
   */
  private loadUserPreferences(): void {
    try {
      const stored = localStorage.getItem('useselft-notification-preferences')
      if (stored) {
        const parsed = JSON.parse(stored)
        this.userPreferences = { ...this.userPreferences, ...parsed }
        console.log('🔊 Loaded notification preferences from localStorage')
      }
    } catch (error) {
      console.warn('🔊 Failed to load notification preferences:', error)
    }
  }

  /**
   * Save user preferences to localStorage
   */
  private saveUserPreferences(): void {
    try {
      localStorage.setItem('useselft-notification-preferences', JSON.stringify(this.userPreferences))
    } catch (error) {
      console.warn('🔊 Failed to save notification preferences:', error)
    }
  }

  /**
   * Check if notifications are supported
   */
  isSupported(): boolean {
    return !!(window.AudioContext || (window as any).webkitAudioContext)
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }
    this.isAudioInitialized = false
    console.log('🔊 NotificationSoundService destroyed')
  }
}

// Global instance
export const notificationSoundService = new NotificationSoundService()